import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';

/**
 * Evita que un fallo en una pantalla deje el panel en blanco.
 *
 * Sin esto, un error de render en el menú tumbaría toda la aplicación y el
 * comercio no podría ni cerrar sesión. Con la barrera, el resto del panel
 * sigue funcionando y la pantalla rota ofrece reintentar.
 *
 * Debe ser una clase: React todavía no permite capturar errores desde
 * componentes de función.
 */
export class BarreraError extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Error no controlado en el panel:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="grid min-h-[60vh] place-items-center px-4">
        <div className="max-w-md rounded-kick bg-white p-8 text-center shadow-card">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-600">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-xl text-kick-dark">Algo salió mal en esta pantalla</h1>
          <p className="mt-2 text-sm text-kick-gray">{this.state.error.message}</p>

          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => this.setState({ error: null })}
              className="inline-flex items-center gap-2 rounded-kick border border-kick-gray/30 px-4 py-2.5 text-sm font-semibold text-kick-ink transition hover:bg-kick-light"
            >
              <RotateCw className="h-4 w-4" />
              Reintentar
            </button>
            <button onClick={() => window.location.assign('/')} className="btn-kick px-4 py-2.5 text-sm">
              Ir al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
}
